import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Leaf, Check, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal, EASE } from "@/lib/motion";
import { loadRazorpay } from "@/lib/razorpay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INCLUDES = [
  "A one-on-one consultation — completely free",
  "A personalised blend of up to 7 flower essences",
  "Guidance on dosage, duration and follow-up",
  "Ongoing emotional wellness support",
];

export default function PaymentSection({ onBookNow }) {
  const [cfg, setCfg] = useState({ enabled: false, amount_inr: 1500, key_id: "" });
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/payments/config`)
      .then((r) => setCfg(r.data))
      .catch(() => {});
  }, []);

  const handlePay = async () => {
    if (!cfg.enabled) {
      toast.info("Online payments are being set up. Please book a free consultation — we'll share remedy details and a secure payment link personally.");
      return;
    }
    setPaying(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) {
        toast.error("Could not load the payment window. Please try again.");
        return;
      }
      const { data } = await axios.post(`${API}/payments/create-order`, {});
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "Bloom & Heal",
        description: "Personalised Bach Flower Remedy",
        order_id: data.order_id,
        theme: { color: "#0B302F" },
        handler: async (res) => {
          try {
            await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success("Payment received — thank you! We'll be in touch about your remedy.");
          } catch {
            toast.error("Payment could not be verified. If money was deducted, contact us and we'll resolve it.");
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.open();
    } catch (e) {
      toast.error("Something went wrong starting the payment.");
    } finally {
      setPaying(false);
    }
  };

  return (
    <section id="remedy" className="py-20 sm:py-28 bg-[#F6F2EB]/60">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center mb-12">
          <div className="eyebrow mb-3">Consultation free • Remedy priced</div>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#0B302F] tracking-tight">
            Your personalised remedy
          </h2>
          <p className="mt-4 text-[#0B302F]/70 max-w-2xl mx-auto">
            The conversation is always on us. If a remedy feels right for you, it is prepared by hand,
            just for your emotional landscape.
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-6 items-stretch">
          <Reveal className="lg:col-span-7" y={36}>
            <div className="h-full p-8 sm:p-10 rounded-[1.75rem] bg-white border border-[#EAE4DA]">
              <div className="flex items-center gap-2 eyebrow text-[#2A5C5A]">
                <Leaf className="h-4 w-4 text-[#D5B876]" strokeWidth={1.5} /> What&apos;s included
              </div>
              <ul className="mt-6 space-y-3.5">
                {INCLUDES.map((it, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -14 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, ease: EASE, delay: i * 0.08 }}
                    className="flex items-start gap-3 text-[#0B302F]/85"
                  >
                    <span className="mt-0.5 h-6 w-6 rounded-full bg-[#0B302F] text-[#FDFBF7] flex items-center justify-center shrink-0">
                      <Check className="h-3.5 w-3.5" strokeWidth={2} />
                    </span>
                    <span className={i === 0 ? "font-sans font-semibold text-[#0B302F]" : "font-sans"}>{it}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-5" y={36} delay={0.1}>
            <div className="h-full p-8 sm:p-10 rounded-[1.75rem] bg-[#0B302F] text-[#FDFBF7] flex flex-col justify-between">
              <div>
                <div className="eyebrow text-[#D5B876]">Bach Flower Remedy</div>
                <div className="mt-4 flex items-end gap-1">
                  <span className="font-serif text-6xl leading-none">₹{cfg.amount_inr}</span>
                  <span className="text-[#FDFBF7]/60 mb-1.5 text-sm">/ bottle</span>
                </div>
                <p className="mt-4 text-[#FDFBF7]/75 text-sm leading-relaxed">
                  Consultation is free.
                </p>
              </div>

              <div className="mt-8 space-y-3">
                <Button
                  onClick={handlePay}
                  disabled={paying}
                  data-testid="pay-remedy-btn"
                  className="w-full rounded-full bg-[#D5B876] text-[#0B302F] hover:bg-[#E7D5A8] h-12 text-xs tracking-[0.22em] uppercase"
                >
                  {paying ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>Pay for remedy <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} /></>
                  )}
                </Button>
                <Button
                  onClick={onBookNow}
                  variant="outline"
                  data-testid="remedy-book-free"
                  className="w-full rounded-full border-[#FDFBF7]/40 text-[#FDFBF7] hover:bg-[#FDFBF7]/10 bg-transparent h-12 text-xs tracking-[0.22em] uppercase"
                >
                  Book free consultation
                </Button>
                {!cfg.enabled && (
                  <p className="text-[10px] text-[#FDFBF7]/50 text-center tracking-wide">
                    Secure online payment coming soon.
                  </p>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
